import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Card,
  Fade,
  useTheme,
  alpha,
  Select,
  MenuItem,
  Slider,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { loadModules } from 'esri-loader';
import { useEffect, useRef, useState, useCallback } from 'react';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';
import '../template.module.css';
import SendIcon from '@mui/icons-material/Send';
import MapIcon from '@mui/icons-material/Map';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TuneIcon from '@mui/icons-material/Tune';

import LayersIcon from '@mui/icons-material/Layers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ErrorIcon from '@mui/icons-material/Error';
import socketService from '../services/socketService';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Markdown renderer component for assistant messages
const MarkdownMessage = ({ content, isUserMessage }) => {
  if (isUserMessage) {
    // User messages remain as plain text
    return (
      <Typography
        variant='body2'
        sx={{
          lineHeight: 1.5,
          fontSize: '0.875rem',
        }}
      >
        {content}
      </Typography>
    );
  }

  // Assistant messages are rendered as markdown
  return (
    <Box
      sx={{
        fontSize: '0.875rem',
        lineHeight: 1.5,
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '0.25rem 0',
          fontWeight: 600,
          color: 'inherit',
        },
        '& h1': { fontSize: '1.125rem' },
        '& h2': { fontSize: '1rem' },
        '& h3': { fontSize: '0.95rem' },
        '& p': {
          margin: '0.25rem 0',
          lineHeight: 1.5,
        },
        '& ul, & ol': {
          margin: '0.25rem 0',
          paddingLeft: '1.25rem',
        },
        '& li': {
          margin: '0.125rem 0',
        },
        '& code': {
          backgroundColor: 'rgba(0,0,0,0.08)',
          padding: '0.125rem 0.25rem',
          borderRadius: '0.25rem',
          fontSize: '0.8em',
          fontFamily: 'monospace',
        },
        '& pre': {
          backgroundColor: 'rgba(0,0,0,0.04)',
          padding: '0.5rem',
          borderRadius: '0.375rem',
          overflow: 'auto',
          margin: '0.25rem 0',
          fontSize: '0.8em',
        },
        '& blockquote': {
          borderLeft: '3px solid #e0e0e0',
          paddingLeft: '0.75rem',
          margin: '0.25rem 0',
          fontStyle: 'italic',
          opacity: 0.8,
        },
        '& a': {
          color: '#1976d2',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        '& strong': {
          fontWeight: 600,
        },
        '& em': {
          fontStyle: 'italic',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          margin: '0.25rem 0',
          fontSize: '0.8em',
        },
        '& th, & td': {
          border: '1px solid #e0e0e0',
          padding: '0.375rem',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: 'rgba(0,0,0,0.04)',
          fontWeight: 600,
        },
      }}
    >
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={{
          // Customize heading components
          h1: ({ children }) => (
            <Typography
              variant='subtitle1'
              component='h1'
              sx={{ fontSize: '1.125rem', margin: '0.25rem 0' }}
            >
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography
              variant='subtitle2'
              component='h2'
              sx={{ fontSize: '1rem', margin: '0.25rem 0' }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography
              variant='body1'
              component='h3'
              sx={{ fontSize: '0.95rem', fontWeight: 600, margin: '0.25rem 0' }}
            >
              {children}
            </Typography>
          ),
          // Customize paragraph component
          p: ({ children }) => (
            <Typography
              variant='body2'
              component='p'
              sx={{ margin: '0.25rem 0', lineHeight: 1.5 }}
            >
              {children}
            </Typography>
          ),
          // Customize code components
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <Box
                  component='code'
                  sx={{
                    backgroundColor: 'rgba(0,0,0,0.08)',
                    padding: '0.125rem 0.25rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.8em',
                    fontFamily: 'monospace',
                  }}
                >
                  {children}
                </Box>
              );
            }
            return (
              <Box
                component='pre'
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  overflow: 'auto',
                  margin: '0.25rem 0',
                  fontSize: '0.8em',
                }}
              >
                <Box
                  component='code'
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.8em',
                  }}
                >
                  {children}
                </Box>
              </Box>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

const MapComponent = ({
  mapRef,
  isMapLoaded,
  isCategorical,
  uniqueValueInfos,
  currentPage,
  handlePrevPage,
  handleNextPage,
  mapSettings,
  selectedBlock,
  mapError,
  setMapError,
}) => {
  const pageSize = 1000;

  return (
    <Card
      sx={{
        width: { xs: '100%', md: '60%' },
        minHeight: { xs: 200, sm: 250, md: '100%' },
        height: { xs: '40vh', sm: '40vh', md: '100%' },
        maxHeight: { xs: '50vh', sm: '50vh', md: '100%' },
        minWidth: 0,
        flexBasis: { xs: 'auto', md: '60%' },
        flexGrow: 0,
        flexShrink: 0,
        maxWidth: { xs: '100%', md: '60%' },

        position: 'relative',

        borderRadius: '12px 0 0 12px',
        overflow: 'hidden',
        boxShadow: 'none',
        background:
          mapSettings.mapBackgroundColor ||
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      {/* Simulate Error Button */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          zIndex: 1100,
        }}
      >
        <Button
          variant='contained'
          color='error'
          size='small'
          onClick={() => setMapError(true)}
        >
          Simulate Map Error
        </Button>
      </Box>

      {/* Error Overlay */}
      {mapError && (
        <Fade in={mapError}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background:
                'linear-gradient(135deg, rgba(234, 102, 102, 0.9) 0%, rgba(162, 75, 75, 0.9) 100%)',
              backdropFilter: 'blur(10px)',
              zIndex: 1200,
            }}
          >
            <ErrorOutlineIcon sx={{ color: '#fff', fontSize: 60, mb: 2 }} />
            <Typography
              variant='h6'
              sx={{
                color: '#ffffff',
                fontWeight: 600,
                textAlign: 'center',
                mb: 1,
              }}
            >
              Map Loading Error
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: alpha('#ffffff', 0.8),
                textAlign: 'center',
                mb: 2,
              }}
            >
              An error occurred while loading the map. Please try again or
              contact support.
            </Typography>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => setMapError(false)}
            >
              Dismiss
            </Button>
          </Box>
        </Fade>
      )}

      {!isMapLoaded && !mapError && (
        <Fade in={!isMapLoaded}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background:
                'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
              backdropFilter: 'blur(10px)',
              zIndex: 1000,
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: '#ffffff',
                mb: 3,
              }}
            />
            <Typography
              variant='h6'
              sx={{
                color: '#ffffff',
                fontWeight: 600,
                textAlign: 'center',
                mb: 1,
              }}
            >
              Loading Interactive Map
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: alpha('#ffffff', 0.8),
                textAlign: 'center',
              }}
            >
              Preparing your geospatial visualization...
            </Typography>
          </Box>
        </Fade>
      )}

      <div
        ref={mapRef}
        style={{
          height: '100%',
          width: '100%',
          visibility: isMapLoaded && !mapError ? 'visible' : 'hidden',
          borderRadius: '12px 0 0 12px',
        }}
      />

      {/* Enhanced Pagination Controls */}
      {isCategorical && isMapLoaded && !mapError && (
        <Fade in={isCategorical && isMapLoaded && !mapError}>
          <Card
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 3,
              py: 1.5,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
          >
            <IconButton
              disabled={currentPage === 0}
              onClick={handlePrevPage}
              sx={{
                bgcolor:
                  currentPage === 0
                    ? 'transparent'
                    : mapSettings.accentColor || '#667eea',
                color: currentPage === 0 ? '#ccc' : 'white',
                '&:hover': {
                  bgcolor: currentPage === 0 ? 'transparent' : '#5a67d8',
                },
                width: 40,
                height: 40,
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            <Chip
              label={`Page ${currentPage + 1} • ${
                uniqueValueInfos.length
              } items`}
              sx={{
                bgcolor: '#f8f9fa',
                color: '#495057',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            />

            <IconButton
              onClick={handleNextPage}
              disabled={uniqueValueInfos.length < pageSize}
              sx={{
                bgcolor:
                  uniqueValueInfos.length < pageSize
                    ? 'transparent'
                    : mapSettings.accentColor || '#667eea',
                color: uniqueValueInfos.length < pageSize ? '#ccc' : 'white',
                '&:hover': {
                  bgcolor:
                    uniqueValueInfos.length < pageSize
                      ? 'transparent'
                      : '#5a67d8',
                },
                width: 40,
                height: 40,
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Card>
        </Fade>
      )}
    </Card>
  );
};

const ChatComponent = ({
  chatMessages,
  chatEndRef,
  messageInput,
  setMessageInput,
  formatTime,
  getMessageIcon,
  getMessageColor,
  handleSendMessage,
  mapSettings,
  setMapSettings,
  changeSelectedBlock,
  handleFieldDisplay,
  isTyping,
  setIsTyping,
  selectedBlock,
  defaultSettings,
  legendSettings,
  setLegendSettings,
  generateLegendColors,
  applyCustomLegendColors,
  applyLegendTitle,
  handleColorChange,
  onSaveCustomMap,
  sectionKey,
  webmapID,
  setChatMessages,
  toggleSectionExpanded,
  isSectionExpanded,
  handleSelectFieldFromChoices,
}) => {
  const [showMapSettings, setShowMapSettings] = useState(false);
  const [isEndVisible, setIsEndVisible] = useState(true);
  const chatContainerRef = useRef(null);

  // Check if the end of chat is visible
  const checkEndVisible = () => {
    if (chatContainerRef.current && chatEndRef.current) {
      const container = chatContainerRef.current;
      const chatEnd = chatEndRef.current;

      const containerRect = container.getBoundingClientRect();
      const chatEndRect = chatEnd.getBoundingClientRect();

      // Check if the end element is fully visible in the container
      setIsEndVisible(
        chatEndRect.bottom <= containerRect.bottom + 20, // Add a small buffer
      );
    }
  };

  // Scroll handler to check visibility
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkEndVisible);
      // Initial check
      checkEndVisible();

      return () => {
        container.removeEventListener('scroll', checkEndVisible);
      };
    }
  }, []);

  // Check end visibility when chat messages change
  useEffect(() => {
    checkEndVisible();
  }, [chatMessages]);

  // // Scroll to end function
  const scrollToEnd = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Card
      sx={{
        width: { xs: '100%', md: '40%' },
        minHeight: { xs: 200, sm: 250, md: '100%' },
        height: { xs: '60vh', sm: '60vh', md: '100%' },
        maxHeight: { xs: '100%', sm: '100%', md: '100%' },
        minWidth: 0,
        flexBasis: { xs: 'auto', md: '40%' },
        flexGrow: 1,
        flexShrink: 1,
        maxWidth: { xs: '100%', md: '40%' },
        overflow: 'auto',

        borderRadius: { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        boxShadow: 'none',
        background: '#ffffff',
      }}
    >
      {/* Enhanced Chat Header */}
      <Box
        sx={{
          p: 1,
          background:
            mapSettings.headerColor ||
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                width: 40,
                height: 40,
              }}
            >
              <SmartToyIcon />
            </Avatar>
            <Box>
              <Typography
                variant='h6'
                sx={{ fontWeight: 700, fontSize: '1.1rem' }}
              >
                MCP Symbolization
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.9 }}>
                AI-Powered Symbolization
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Settings Toggle Button */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.1)',
            zIndex: 10,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.2)',
            },
            cursor: 'pointer',
          }}
          onClick={() => setShowMapSettings(!showMapSettings)}
        >
          <TuneIcon />
        </IconButton>
      </Box>

      {/* Map Settings Panel */}
      {showMapSettings && (
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e9ecef',
            background: '#f8f9fa',
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              mb: 2,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LayersIcon fontSize='small' color='primary' />
            Map Configuration
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant='caption' sx={{ mb: 0.5, display: 'block' }}>
              Layer Opacity
            </Typography>
            <Slider
              value={mapSettings.layerOpacity * 100}
              onChange={(_, newValue) =>
                setMapSettings(prev => ({
                  ...prev,
                  layerOpacity: newValue / 100,
                }))
              }
              aria-labelledby='opacity-slider'
              valueLabelDisplay='auto'
              step={5}
              marks
              min={0}
              max={100}
              size='small'
              sx={{ color: mapSettings.accentColor || '#667eea' }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size='small' sx={{ mb: 2 }}>
              <InputLabel>Basemap Style</InputLabel>
              <Select
                value={mapSettings.basemap}
                label='Basemap Style'
                onChange={e =>
                  setMapSettings(prev => ({ ...prev, basemap: e.target.value }))
                }
                size='small'
              >
                <MenuItem value='gray-vector'>Gray</MenuItem>
                <MenuItem value='streets-vector'>Streets</MenuItem>
                <MenuItem value='satellite'>Satellite</MenuItem>
                <MenuItem value='hybrid'>Hybrid</MenuItem>
                <MenuItem value='dark-gray-vector'>Dark Gray</MenuItem>
                <MenuItem value='osm'>OpenStreetMap</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Legend Customization - Only for selected blocks */}
          {selectedBlock && !defaultSettings && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant='caption'
                sx={{ mb: 1, display: 'block', fontWeight: 600 }}
              >
                Legend Customization
              </Typography>

              {/* Legend Title */}
              <TextField
                fullWidth
                size='small'
                label='Legend Title'
                value={
                  legendSettings.title ||
                  selectedBlock.mapTitle ||
                  selectedBlock.title
                }
                onChange={e => {
                  const newTitle = e.target.value;
                  setLegendSettings(prev => ({
                    ...prev,
                    title: newTitle,
                    isCustomized: true,
                  }));
                  // Apply title change immediately
                  applyLegendTitle(newTitle);
                }}
                sx={{ mb: 2 }}
                placeholder='Enter custom legend title...'
              />

              {/* Legend Colors */}
              <Typography variant='caption' sx={{ mb: 1, display: 'block' }}>
                Legend Colors
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {legendSettings.colors.length > 0 ? (
                  legendSettings.colors.map((color, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Typography
                        variant='caption'
                        sx={{ minWidth: '80px', fontSize: '0.75rem' }}
                      >
                        {color.label}
                      </Typography>
                      <input
                        type='color'
                        value={color.color}
                        onChange={e => {
                          const newColors = [...legendSettings.colors];
                          newColors[index].color = e.target.value;
                          setLegendSettings(prev => ({
                            ...prev,
                            colors: newColors,
                            isCustomized: true,
                          }));
                        }}
                        style={{
                          width: '30px',
                          height: '30px',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <Typography
                        variant='caption'
                        sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                      >
                        {color.range}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => generateLegendColors()}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Generate Legend Colors
                  </Button>
                )}

                {/* Apply Colors Button */}
                {legendSettings.colors.length > 0 && (
                  <Button
                    size='small'
                    variant='contained'
                    onClick={applyCustomLegendColors}
                    sx={{
                      alignSelf: 'flex-start',
                      mt: 1,
                      background: mapSettings.accentColor || '#667eea',
                    }}
                  >
                    Apply Colors to Map
                  </Button>
                )}

                {/* Color Preview */}
                {legendSettings.colors.length > 0 && (
                  <Box
                    sx={{ mt: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}
                  >
                    <Typography
                      variant='caption'
                      sx={{ mb: 1, display: 'block', fontWeight: 600 }}
                    >
                      Color Preview
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {legendSettings.colors.map((color, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: '20px',
                            height: '20px',
                            bgcolor: color.color,
                            borderRadius: '2px',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              transition: 'transform 0.2s',
                            },
                          }}
                          title={`${color.label}: ${color.range}`}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Enhanced Chat Messages */}
      <Box
        ref={chatContainerRef}
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
          position: 'relative',
        }}
      >
        {chatMessages.map((message, index) => (
          <Fade in={true} key={index} timeout={300 + index * 100}>
            <Box
              sx={{
                alignSelf:
                  message.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  flexDirection:
                    message.sender === 'user' ? 'row-reverse' : 'row',
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor:
                      message.sender === 'user'
                        ? mapSettings.accentColor || '#667eea'
                        : getMessageColor(message.type),
                    fontSize: '0.875rem',
                    ...(message.type === 'error' && {
                      border: '2px solid #f44336',
                      boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.2)',
                    }),
                    flexShrink: 0,
                  }}
                >
                  {getMessageIcon(message.sender, message.type)}
                </Avatar>

                <Card
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2, md: 2.5 }, // Responsive padding
                    borderRadius: 3,
                    maxWidth: '100%',
                    width: 'auto',
                    minWidth: 0,
                    background:
                      message.sender === 'user'
                        ? mapSettings.headerColor ||
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : message.type === 'error'
                        ? '#fff5f5'
                        : '#ffffff',
                    color: message.sender === 'user' ? 'white' : '#2c3e50',
                    border:
                      message.sender === 'user'
                        ? 'none'
                        : message.type === 'error'
                        ? '1px solid #fecaca'
                        : '1px solid #e9ecef',
                    boxShadow:
                      message.sender === 'user'
                        ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                        : message.type === 'error'
                        ? '0 2px 8px rgba(244, 67, 54, 0.15)'
                        : '0 2px 8px rgba(0,0,0,0.08)',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    flexShrink: 1,
                  }}
                >
                  {message.type === 'info' && message.fieldChoices ? (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Typography variant='subtitle2' sx={{ mb: 1 }}>
                        {message.text}
                      </Typography>
                      {/* 1. Top Picks/Best Matches */}
                      {message.fieldChoices?.topPicks?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Box
                            onClick={() =>
                              toggleSectionExpanded(index, 'topPicks')
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{ fontWeight: 600 }}
                            >
                              Top Picks/Best Matches
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{ ml: 1, color: '#6b7280' }}
                            >
                              {isSectionExpanded(index, 'topPicks') ? '▼' : '▶'}
                            </Typography>
                          </Box>
                          {isSectionExpanded(index, 'topPicks') && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}
                            >
                              {message.fieldChoices.topPicks.map(
                                (item, idx) => (
                                  <Box
                                    key={`tp-${idx}-${item.originalIndex}`}
                                    onClick={() =>
                                      handleSelectFieldFromChoices(
                                        item.originalIndex,
                                        index,
                                      )
                                    }
                                    sx={{
                                      px: 1,
                                      py: 0.5,
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      background: '#fff',
                                      '&:hover': {
                                        background: '#f9fafb',
                                      },
                                      fontSize: '0.8rem',
                                    }}
                                    title={item?.fieldName?.alias}
                                  >
                                    <Typography
                                      variant='body2'
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {item?.fieldName?.alias || 'Field'}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      sx={{ color: '#6b7280' }}
                                    >
                                      {item?.layer}
                                    </Typography>
                                  </Box>
                                ),
                              )}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* 2. Based on Year */}
                      {message.fieldChoices?.basedOnYear &&
                        Object.keys(message.fieldChoices.basedOnYear).length >
                          0 && (
                          <Box sx={{ mb: 1 }}>
                            <Box
                              onClick={() =>
                                toggleSectionExpanded(index, 'basedOnYear')
                              }
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{ fontWeight: 600 }}
                              >
                                Based on Year
                              </Typography>
                              <Typography
                                variant='caption'
                                sx={{ ml: 1, color: '#6b7280' }}
                              >
                                {isSectionExpanded(index, 'basedOnYear')
                                  ? '▼'
                                  : '▶'}
                              </Typography>
                            </Box>
                            {isSectionExpanded(index, 'basedOnYear') && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                }}
                              >
                                {(
                                  message.fieldChoices.sortedYearKeys ||
                                  Object.keys(
                                    message.fieldChoices.basedOnYear,
                                  ).sort((a, b) => {
                                    const ya = Number(a);
                                    const yb = Number(b);
                                    if (ya === 2025 && yb !== 2025) return -1;
                                    if (yb === 2025 && ya !== 2025) return 1;
                                    return yb - ya;
                                  })
                                ).map(yearKey => (
                                  <Box key={`yr-${yearKey}`} sx={{ mb: 0.5 }}>
                                    <Typography
                                      variant='caption'
                                      sx={{ color: '#6b7280' }}
                                    >
                                      {yearKey}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mt: 0.5,
                                      }}
                                    >
                                      {message.fieldChoices.basedOnYear[
                                        yearKey
                                      ].map((item, idx) => (
                                        <Box
                                          key={`by-${yearKey}-${idx}-${item.originalIndex}`}
                                          onClick={() =>
                                            handleSelectFieldFromChoices(
                                              item.originalIndex,
                                              index,
                                            )
                                          }
                                          sx={{
                                            px: 1,
                                            py: 0.5,
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            background: '#fff',
                                            '&:hover': {
                                              background: '#f9fafb',
                                            },
                                            fontSize: '0.8rem',
                                          }}
                                          title={item?.fieldName?.alias}
                                        >
                                          <Typography
                                            variant='body2'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            {item?.fieldName?.alias || 'Field'}
                                          </Typography>
                                          <Typography
                                            variant='caption'
                                            sx={{ color: '#6b7280' }}
                                          >
                                            {item?.layer}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}

                      {/* 3. Based on Geographies */}
                      {message.fieldChoices?.basedOnGeographies &&
                        Object.keys(message.fieldChoices.basedOnGeographies)
                          .length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Box
                              onClick={() =>
                                toggleSectionExpanded(
                                  index,
                                  'basedOnGeographies',
                                )
                              }
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{ fontWeight: 600 }}
                              >
                                Based on Geographies
                              </Typography>
                              <Typography
                                variant='caption'
                                sx={{ ml: 1, color: '#6b7280' }}
                              >
                                {isSectionExpanded(index, 'basedOnGeographies')
                                  ? '▼'
                                  : '▶'}
                              </Typography>
                            </Box>
                            {isSectionExpanded(index, 'basedOnGeographies') && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                }}
                              >
                                {(
                                  message.fieldChoices.geoOrder || [
                                    'block',
                                    'tract',
                                    'zip',
                                    'county',
                                    'cbsa',
                                    'dma',
                                    'congressional district',
                                    'state',
                                    'country',
                                  ]
                                ).map(geoType => {
                                  const geoFields =
                                    message.fieldChoices.basedOnGeographies[
                                      geoType
                                    ] || [];
                                  if (geoFields.length === 0) return null;

                                  return (
                                    <Box
                                      key={`geo-${geoType}`}
                                      sx={{ mb: 0.5 }}
                                    >
                                      <Typography
                                        variant='caption'
                                        sx={{
                                          color: '#6b7280',
                                          textTransform: 'capitalize',
                                        }}
                                      >
                                        {geoType}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          gap: 1,
                                          mt: 0.5,
                                        }}
                                      >
                                        {geoFields.map((item, idx) => (
                                          <Box
                                            key={`bg-${geoType}-${idx}-${item.originalIndex}`}
                                            onClick={() =>
                                              handleSelectFieldFromChoices(
                                                item.originalIndex,
                                                index,
                                              )
                                            }
                                            sx={{
                                              px: 1,
                                              py: 0.5,
                                              border: '1px solid #e5e7eb',
                                              borderRadius: '8px',
                                              cursor: 'pointer',
                                              background: '#fff',
                                              '&:hover': {
                                                background: '#f9fafb',
                                              },
                                              fontSize: '0.8rem',
                                            }}
                                            title={item?.fieldName?.alias}
                                          >
                                            <Typography
                                              variant='body2'
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {item?.fieldName?.alias ||
                                                'Field'}
                                            </Typography>
                                            <Typography
                                              variant='caption'
                                              sx={{ color: '#6b7280' }}
                                            >
                                              {item?.layer}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        )}

                      {/* 4. Show All Variants */}
                      {message.fieldChoices?.showAllVariants?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Box
                            onClick={() =>
                              toggleSectionExpanded(index, 'showAllVariants')
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{ fontWeight: 600 }}
                            >
                              Show All Variants
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{ ml: 1, color: '#6b7280' }}
                            >
                              {isSectionExpanded(index, 'showAllVariants')
                                ? '▼'
                                : '▶'}
                            </Typography>
                          </Box>
                          {isSectionExpanded(index, 'showAllVariants') && (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                  'repeat(auto-fill, minmax(220px, 1fr))',
                                gap: 1,
                              }}
                            >
                              {message.fieldChoices.showAllVariants.map(
                                (item, idx) => (
                                  <Box
                                    key={`all-${idx}-${item.originalIndex}`}
                                    onClick={() =>
                                      handleSelectFieldFromChoices(
                                        item.originalIndex,
                                        index,
                                      )
                                    }
                                    sx={{
                                      px: 1,
                                      py: 0.75,
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      background: '#fff',
                                      '&:hover': {
                                        background: '#f9fafb',
                                      },
                                    }}
                                    title={item?.fieldName?.alias}
                                  >
                                    <Typography
                                      variant='body2'
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {item?.fieldName?.alias || 'Field'}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      sx={{ color: '#6b7280' }}
                                    >
                                      {item?.layer}
                                    </Typography>
                                  </Box>
                                ),
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <>
                      <MarkdownMessage
                        content={message.text}
                        isUserMessage={message.sender === 'user'}
                      />
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          mt: 1,
                          opacity: 0.7,
                          textAlign:
                            message.sender === 'user' ? 'left' : 'right',
                          fontSize: '0.75rem',
                        }}
                      >
                        {formatTime(message.timestamp)}
                      </Typography>
                    </>
                  )}
                </Card>
              </Box>
            </Box>
          </Fade>
        ))}

        {/* Show typing animation */}
        {isTyping && <TypingAnimation mapSettings={mapSettings} />}

        <div ref={chatEndRef} />

        {/* Scroll to bottom button - only visible when end is not in view */}
        {!isEndVisible && (
          <Fade in={!isEndVisible}>
            <IconButton
              onClick={scrollToEnd}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                backgroundColor: mapSettings.accentColor || '#667eea',
                color: 'white',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                width: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: '#5a67d8',
                },
                zIndex: 10,
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Fade>
        )}
      </Box>

      {/* Enhanced Chat Input */}
      <Box
        sx={{
          p: 2.5,
          borderTop: '1px solid #e9ecef',
          background: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mb: 1.5,
          }}
        >
          <Button
            variant='contained'
            sx={{
              borderRadius: 2,
              background: mapSettings.accentColor || '#667eea',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              px: 3,
              py: 1,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b5b95 100%)',
              },
            }}
            onClick={() => {
              changeSelectedBlock({
                group: 'Block Group',
                title: '2024 Total Population (Esri)',
                status: false,
                type: 'esriFieldTypeInteger',
                prefix: null,
                suffix: null,
                outfields: ['TOTPOP_CY'],
                serviceURL:
                  'https://utility.arcgis.com/usrsvcs/servers/e8884cc26c4f4ed9bb6676b7755f7100/rest/services/USA_Demographics_and_Boundaries_2024/MapServer/12',
                boundaries: false,
              });
            }}
          >
            Load Demo Block
          </Button>

          <Button
            variant='outlined'
            sx={{
              borderRadius: 2,
              borderColor: mapSettings.accentColor || '#667eea',
              color: mapSettings.accentColor || '#667eea',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: '#5a67d8',
                color: '#5a67d8',
                background: 'rgba(102, 126, 234, 0.04)',
              },
            }}
            onClick={() => {
              // Simulate the displayFieldsInUI action with different field types
              const demoFields = [
                {
                  serviceURL:
                    'https://utility.arcgis.com/usrsvcs/servers/e8884cc26c4f4ed9bb6676b7755f7100/rest/services/USA_Demographics_and_Boundaries_2024/MapServer/12',
                  title: '2024 Median Household Income (Esri)',
                  group: 'Block Group',
                  outfields: ['MEDHINC_CY'],
                },
                {
                  serviceURL:
                    'https://utility.arcgis.com/usrsvcs/servers/e8884cc26c4f4ed9bb6676b7755f7100/rest/services/USA_Demographics_and_Boundaries_2024/MapServer/12',
                  title: '2024 Total Population (Esri)',
                  group: 'Block Group',
                  outfields: ['TOTPOP_CY'],
                },
                {
                  serviceURL:
                    'https://utility.arcgis.com/usrsvcs/servers/e8884cc26c4f4ed9bb6676b7755f7100/rest/services/USA_Demographics_and_Boundaries_2024/MapServer/12',
                  title: '2024 Housing Units (Esri)',
                  group: 'Block Group',
                  outfields: ['TOTHH_CY'],
                },
              ];

              // Cycle through demo fields
              const randomIndex = Math.floor(Math.random() * demoFields.length);
              const mockFieldData = demoFields[randomIndex];
              handleFieldDisplay(mockFieldData);
            }}
          >
            Test Field Display
          </Button>

          {/* Save Custom Map Button */}
          <Button
            variant='contained'
            sx={{
              borderRadius: 2,
              background: '#4caf50',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              px: 3,
              py: 1,
              '&:hover': {
                background: '#45a049',
              },
            }}
            onClick={() => {
              if (onSaveCustomMap) {
                const customMapData = {
                  sectionKey,
                  mapSettings,
                  legendSettings,
                  selectedBlock,
                  webmapID,
                };
                onSaveCustomMap(customMapData);

                // Add success message to chat
                setChatMessages(prev => [
                  ...prev,
                  {
                    text: `✅ Custom map configuration saved successfully! The map settings, legend colors, and field data have been saved to the template.`,
                    sender: 'system',
                    timestamp: new Date(),
                    type: 'success',
                  },
                ]);
              }
            }}
          >
            Save Custom Map
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            placeholder='Ask me about the map data or customize the map...'
            size='small'
            multiline
            maxRows={3}
            value={messageInput}
            onChange={e => setMessageInput(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#f8f9fa',
                '& fieldset': {
                  borderColor: '#e9ecef',
                },
                '&:hover fieldset': {
                  borderColor: mapSettings.accentColor || '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: mapSettings.accentColor || '#667eea',
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!messageInput.trim()}
            sx={{
              background:
                mapSettings.headerColor ||
                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              width: 44,
              height: 44,
              borderRadius: 2.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b5b95 100%)',
              },
              // '&.Mui-disabled': {
              //   background: '#e9ecef',
              //   color: '#6c757d',
              // },
            }}
          >
            <SendIcon fontSize='small' />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
};

const TypingAnimation = ({ mapSettings }) => {
  return (
    <Fade in={true}>
      <Box
        sx={{
          alignSelf: 'flex-start',
          maxWidth: '85%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: '#2196f3', // Use a fixed color for typing animation
              fontSize: '0.875rem',
            }}
          >
            <SmartToyIcon />
          </Avatar>

          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: '#ffffff',
              color: '#2c3e50',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              minWidth: '120px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#6c757d',
                  animation: 'typing 1.4s infinite ease-in-out',
                  '&:nth-of-type(1)': { animationDelay: '0s' },
                  '&:nth-of-type(2)': { animationDelay: '0.2s' },
                  '&:nth-of-type(3)': { animationDelay: '0.4s' },
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#6c757d',
                  animation: 'typing 1.4s infinite ease-in-out',
                  '&:nth-of-type(1)': { animationDelay: '0s' },
                  '&:nth-of-type(2)': { animationDelay: '0.2s' },
                  '&:nth-of-type(3)': { animationDelay: '0.4s' },
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#6c757d',
                  animation: 'typing 1.4s infinite ease-in-out',
                  '&:nth-of-type(1)': { animationDelay: '0s' },
                  '&:nth-of-type(2)': { animationDelay: '0.2s' },
                  '&:nth-of-type(3)': { animationDelay: '0.4s' },
                }}
              />
            </Box>
            <style>
              {`
                @keyframes typing {
                  0%, 60%, 100% {
                    transform: translateY(0);
                    opacity: 0.4;
                  }
                  30% {
                    transform: translateY(-10px);
                    opacity: 1;
                  }
                }
              `}
            </style>
          </Card>
        </Box>
      </Box>
    </Fade>
  );
};

export default function McpSymbolization({
  webmapID,
  defaultSettingsFlag,
  token,
  setMapConfig = () => {},
  sectionKey,
  setMcpSymbolization,
  onSaveCustomMap,
  customMapConfig = null,
  onAddFieldToTemplate = null,
}) {
  const mapRef = useRef(null);
  const chatEndRef = useRef(null);
  const mapViewRef = useRef(null); // Store reference to the map view
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [uniqueValueInfos, setUniqueValueInfos] = useState([]);
  const [isCategorical, setIsCategorical] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [selectedBlock, setSelectedBlock] = useState(
    customMapConfig?.selectedBlock?.customMapConfig?.selectedBlock ||
      customMapConfig?.selectedBlock ||
      null,
  );
  const [defaultSettings, setDefaultSettings] = useState(
    customMapConfig?.type === 'custom' ? false : defaultSettingsFlag,
  );
  const [conversationId, setConversationId] = useState(null);
  const [llmWebmapID, setLlmWebmapID] = useState(webmapID);
  const [isTyping, setIsTyping] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Map());

  const [mapSettings, setMapSettings] = useState(
    customMapConfig?.mapSettings
      ? {
          basemap: 'gray-vector',
          layerOpacity: 0.6,
          accentColor: '#667eea',
          headerColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          mapBackgroundColor:
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          legendPosition: 'top-right',
          zoomLevel: 14,
          mouseWheelZoom: false,
          showLegend: true,
          classificationType: 'quantile',
          classBreaksCount: 5,
          ...customMapConfig.mapSettings,
        }
      : {
          basemap: 'gray-vector',
          layerOpacity: 0.6,
          accentColor: '#667eea',
          headerColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          mapBackgroundColor:
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          legendPosition: 'top-right',
          zoomLevel: 14,
          mouseWheelZoom: false,
          showLegend: true,
          classificationType: 'quantile',
          classBreaksCount: 5,
        },
  );

  const [legendSettings, setLegendSettings] = useState(
    customMapConfig?.legendSettings
      ? {
          title: '',
          colors: [],
          isCustomized: false,
          isProgrammaticChange: false,
          ...customMapConfig.legendSettings,
        }
      : {
          title: '',
          colors: [],
          isCustomized: false,
          isProgrammaticChange: false,
        },
  );

  const [chatMessages, setChatMessages] = useState([
    {
      text: 'Server Connected! 🗺️',
      sender: 'system',
      timestamp: new Date(),
      type: 'text',
      action: null,
    },
    {
      text: "I'm here to help you explore and understand your geospatial data. You can customize the map by using the settings icon or ask me questions.",
      sender: 'system',
      timestamp: new Date(),
      type: 'text',
      action: null,
    },
  ]);

  const isMounted = useRef(true);

  // Sync state when customMapConfig changes (e.g. template reopened with saved colors) so saved map/legend settings are shown instead of defaults
  const prevConfigRef = useRef(null);
  useEffect(() => {
    if (!customMapConfig) return;
    const hasSavedLegend =
      customMapConfig.legendSettings?.colors?.length > 0 ||
      customMapConfig.legendSettings?.isCustomized;
    const hasSavedMap = customMapConfig.mapSettings && Object.keys(customMapConfig.mapSettings).length > 0;
    if (!hasSavedLegend && !hasSavedMap) return;
    const configSignature = JSON.stringify({
      colors: customMapConfig.legendSettings?.colors,
      mapSettings: customMapConfig.mapSettings,
    });
    if (prevConfigRef.current === configSignature) return;
    prevConfigRef.current = configSignature;

    if (customMapConfig.mapSettings) {
      setMapSettings(prev => ({
        basemap: 'gray-vector',
        layerOpacity: 0.6,
        accentColor: '#667eea',
        headerColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        mapBackgroundColor:
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        legendPosition: 'top-right',
        zoomLevel: 14,
        mouseWheelZoom: false,
        showLegend: true,
        classificationType: 'quantile',
        classBreaksCount: 5,
        ...prev,
        ...customMapConfig.mapSettings,
      }));
    }
    if (customMapConfig.legendSettings) {
      setLegendSettings(prev => ({
        title: '',
        colors: [],
        isCustomized: false,
        isProgrammaticChange: false,
        ...prev,
        ...customMapConfig.legendSettings,
      }));
    }
    const nextBlock =
      customMapConfig.selectedBlock?.customMapConfig?.selectedBlock ||
      customMapConfig.selectedBlock;
    if (nextBlock) {
      setSelectedBlock(nextBlock);
    }
    if (customMapConfig.type === 'custom') {
      setDefaultSettings(false);
    }
  }, [customMapConfig]);

  console.debug('McpSymbolization props:', {
    webmapID,
    defaultSettingsFlag,
    token,
    sectionKey,
    customMapConfig,
  });

  // Helper function to toggle section expansion
  const toggleSectionExpanded = (messageIndex, sectionName) => {
    setExpandedSections(prev => {
      const next = new Map(prev);
      const key = `msg-${messageIndex}`;
      const sectionSet = new Set(next.get(key) || []);
      if (sectionSet.has(sectionName)) {
        sectionSet.delete(sectionName);
      } else {
        sectionSet.add(sectionName);
      }
      next.set(key, sectionSet);
      return next;
    });
  };

  // Helper function to check if section is expanded
  const isSectionExpanded = (messageIndex, sectionName) => {
    const key = `msg-${messageIndex}`;
    const sectionSet = expandedSections.get(key);
    return sectionSet?.has(sectionName) || false;
  };

  const applyLegendTitle = async newTitle => {
    if (!selectedBlock) return;

    try {
      // Get the current map view
      const view = mapViewRef.current;
      if (!view || !view.map) return;

      const map = view.map;
      const customLayer = map.layers.find(l => l.type === 'feature');
      if (!customLayer) return;

      // Update ONLY the main layer title (this appears as the bold title in the legend)
      // Do NOT update renderer.legendOptions.title to avoid changing the subtitle
      customLayer.title = newTitle;

      // Force the layer view to refresh to propagate legend updates
      try {
        const layerView = await view.whenLayerView(customLayer);
        if (layerView && layerView.refresh) {
          layerView.refresh();
        }
      } catch (e) {
        // Fallback to layer refresh
        if (customLayer.refresh) {
          customLayer.refresh();
        }
      }
    } catch (error) {
      console.error('Error applying legend title:', error);
    }
  };

  // Generate legend colors based on current renderer
  const generateLegendColors = async () => {
    if (!selectedBlock || defaultSettings) return;

    try {
      // Get the current map view to extract actual data ranges
      const view = mapViewRef.current;
      if (!view || !view.map) return;

      const map = view.map;
      const customLayer = map.layers.find(l => l.type === 'feature');
      if (!customLayer || !customLayer.renderer) return;

      // Helper to normalize renderer infos and ranges
      const getRendererInfos = renderer => {
        if (renderer?.type === 'class-breaks' && renderer.classBreakInfos) {
          return { infos: renderer.classBreakInfos, kind: 'class-breaks' };
        }
        if (renderer?.type === 'unique-value' && renderer.uniqueValueInfos) {
          return { infos: renderer.uniqueValueInfos, kind: 'unique-value' };
        }
        // Fallback for older references
        if (renderer?.infos) {
          return { infos: renderer.infos, kind: 'generic' };
        }
        return { infos: [], kind: 'none' };
      };

      // Convert ESRI color (array/Color) to hex string for <input type="color">
      const esriColorToHex = c => {
        // ArcGIS colors are usually [r,g,b,a]
        if (Array.isArray(c)) {
          const [r, g, b] = c;
          const toHex = v => v.toString(16).padStart(2, '0');
          return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
        }
        // Some symbols may carry css color string already
        if (typeof c === 'string') {
          // Assume it's already a hex or css string; try to normalize hex-like
          if (c.startsWith('#')) return c;
        }
        // Fallback: default gray
        return '#808080';
      };

      const { infos } = getRendererInfos(customLayer.renderer);

      // Extract actual data ranges from the current renderer
      const colors = [];
      if (infos && infos.length > 0) {
        infos.forEach((info, index) => {
          let range = '';
          if (info.symbol && info.symbol.color) {
            if (
              info.hasOwnProperty('minValue') &&
              info.hasOwnProperty('maxValue')
            ) {
              range = `${info.minValue.toFixed(2)} - ${info.maxValue.toFixed(
                2,
              )}`;
            } else if (info.hasOwnProperty('value')) {
              range = `${info.value}`;
            } else {
              range = `Class ${index + 1}`;
            }

            colors.push({
              label: `Class ${index + 1}`,
              color: esriColorToHex(info.symbol.color),
              range: range,
              index: index,
            });
          }
        });
      }

      // If no renderer info, generate default colors
      if (colors.length === 0) {
        const colorSchemes = {
          quantile: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8'],
          natural: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8'],
          equal: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8'],
          manual: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8'],
        };

        const scheme =
          colorSchemes[mapSettings.classificationType] || colorSchemes.quantile;
        const count = mapSettings.classBreaksCount;

        for (let i = 0; i < count; i++) {
          const colorIndex = Math.floor(
            (i / (count - 1)) * (scheme.length - 1),
          );
          colors.push({
            label: `Class ${i + 1}`,
            color: scheme[colorIndex],
            range: `Range ${i + 1}`,
            index: i,
          });
        }
      }

      setLegendSettings(prev => ({
        ...prev,
        colors,
        isCustomized: true,
      }));

      setChatMessages(prev => [
        ...prev,
        {
          text: `🎨 Generated ${colors.length} legend colors with actual data ranges`,
          sender: 'system',
          timestamp: new Date(),
          type: 'success',
        },
      ]);
    } catch (error) {
      console.error('Error generating legend colors:', error);
      setChatMessages(prev => [
        ...prev,
        {
          text: `❌ Error generating legend colors: ${error.message}`,
          sender: 'system',
          timestamp: new Date(),
          type: 'error',
        },
      ]);
    }
  };

  // Apply custom legend colors to the map
  const applyCustomLegendColors = async () => {
    if (!selectedBlock || defaultSettings || !legendSettings.isCustomized)
      return;

    try {
      // Get the current map view
      const view = mapViewRef.current;
      if (!view || !view.map) return;

      const map = view.map;
      const customLayer = map.layers.find(l => l.type === 'feature');
      if (!customLayer || !customLayer.renderer) return;

      // Instead of creating a new renderer, modify the existing one
      const renderer = customLayer.renderer;

      // Helper to normalize renderer infos
      const getRendererInfos = renderer => {
        if (renderer?.type === 'class-breaks' && renderer.classBreakInfos) {
          return { infos: renderer.classBreakInfos, key: 'classBreakInfos' };
        }
        if (renderer?.type === 'unique-value' && renderer.uniqueValueInfos) {
          return { infos: renderer.uniqueValueInfos, key: 'uniqueValueInfos' };
        }
        // Fallback for older references
        if (renderer?.infos) {
          return { infos: renderer.infos, key: 'infos' };
        }
        return { infos: null, key: null };
      };

      // Convert hex to ESRI rgba array
      const hexToEsriRgba = (hex, alpha = 1) => {
        const h = hex.startsWith('#') ? hex.slice(1) : hex;
        const r = parseInt(h.slice(0, 2), 16) || 0;
        const g = parseInt(h.slice(2, 4), 16) || 0;
        const b = parseInt(h.slice(4, 6), 16) || 0;
        return [r, g, b, alpha];
      };

      const { infos } = getRendererInfos(renderer);

      // Apply custom colors to the existing renderer
      if (infos && legendSettings.colors.length > 0) {
        // ESRI renderer infos are ordered low-to-high, but our colors are typically dark-to-light
        // We need to reverse the color order to match the expected gradient (light for low values, dark for high values)
        const reversedColors = [...legendSettings.colors].reverse();

        // Only apply colors that are actually different from the current renderer
        infos.forEach((info, index) => {
          const colorEntry = reversedColors[index];
          if (colorEntry && colorEntry.color && info.symbol) {
            // Ensure currentColor is properly formatted (ESRI can use either object or array format)
            let currentColor;
            if (info.symbol.color && typeof info.symbol.color === 'object') {
              if (Array.isArray(info.symbol.color)) {
                // Array format: [r, g, b, a]
                currentColor = info.symbol.color;
              } else if (info.symbol.color.r !== undefined) {
                // Object format: { r, g, b, a }
                currentColor = [
                  info.symbol.color.r,
                  info.symbol.color.g,
                  info.symbol.color.b,
                  info.symbol.color.a,
                ];
              } else {
                // Unknown format, use default
                currentColor = [0, 0, 0, 255];
              }
            } else {
              // No color or invalid format, use default
              currentColor = [0, 0, 0, 255];
            }

            const newColor = hexToEsriRgba(
              colorEntry.color,
              currentColor[3] ?? 255,
            );

            // Only apply if the color is different
            if (
              currentColor[0] !== newColor[0] ||
              currentColor[1] !== newColor[1] ||
              currentColor[2] !== newColor[2]
            ) {
              // Update the symbol color in ESRI rgba format
              info.symbol.color = newColor;

              // If it's a simple-fill symbol, also update the outline if it exists
              if (info.symbol.type === 'simple-fill' && info.symbol.outline) {
                const darkerColor = darkenColor(colorEntry.color, 0.3);
                info.symbol.outline.color = hexToEsriRgba(
                  darkerColor,
                  info.symbol.outline.color?.[3] ?? 255,
                );
              }
            } else {
              // do nothing
            }
          }
        });
      }

      // Update the legend title if it's customized
      if (legendSettings.title) {
        // Update ONLY the main layer title (this appears as the bold title in the legend)
        // Do NOT update renderer.legendOptions.title to avoid changing the subtitle
        customLayer.title = legendSettings.title;
      }

      // Reassign a cloned renderer to ensure change detection triggers
      const newRenderer = renderer.clone ? renderer.clone() : renderer;
      customLayer.renderer = newRenderer;

      // Force the layer view to refresh to propagate legend updates
      try {
        const layerView = await view.whenLayerView(customLayer);
        if (layerView && layerView.refresh) {
          // Only refresh if absolutely necessary to avoid flickering
          // The renderer change should be sufficient for most updates
          if (legendSettings.title) {
            layerView.refresh();
          }
        }
      } catch (e) {
        // Fallback to layer refresh only if necessary
        if (customLayer.refresh && legendSettings.title) {
          customLayer.refresh();
        }
      }

      // Add success message
      setChatMessages(prev => [
        ...prev,
        {
          text: `🎨 Legend colors updated successfully!`,
          sender: 'system',
          timestamp: new Date(),
          type: 'success',
        },
      ]);
    } catch (error) {
      console.error('Error applying custom legend colors:', error);
      setChatMessages(prev => [
        ...prev,
        {
          text: `❌ Error updating legend colors: ${error.message}`,
          sender: 'system',
          timestamp: new Date(),
          type: 'error',
        },
      ]);
    }
  };

  // Helper function to darken a color for outlines
  const darkenColor = (color, factor) => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Darken by factor
    const newR = Math.max(0, Math.floor(r * (1 - factor)));
    const newG = Math.max(0, Math.floor(g * (1 - factor)));
    const newB = Math.max(0, Math.floor(b * (1 - factor)));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, '0')}${newG
      .toString(16)
      .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  /**
   * Helper function to handle color changes from LLM actions
   * This function processes color change requests from the LLM service and updates
   * the map visualization and legend colors accordingly.
   *
   * @param {Object} colorData - The color change data object from the LLM
   * @param {string} colorData.action - The type of color change action
   * @param {string} colorData.colorScheme - The color scheme to apply
   * @param {string[]} colorData.colors - Array of hex color codes
   * @param {string} colorData.target - What to change colors for
   * @param {string} colorData.description - Description of the color change request
   * @param {Object} colorData.rangeChanges - Specific range color modifications
   */
  const handleColorChange = colorData => {
    try {
      const { action, colorScheme, colors, target, description, rangeChanges } =
        colorData;

      // Validate required fields
      if (!action || !description) {
        setChatMessages(prev => [
          ...prev,
          {
            text: '❌ Invalid color change data received. Missing required fields.',
            sender: 'system',
            timestamp: new Date(),
            type: 'error',
          },
        ]);
        return;
      }

      // Handle specific range-based color changes
      if (rangeChanges && Object.keys(rangeChanges).length > 0) {
        // Provide current legend context to help with range identification
        const currentLegendContext = getCurrentLegendContext();

        // Add context message to chat for user reference
        setChatMessages(prev => [
          ...prev,
          {
            text: `📊 Current Legend Context:\n${currentLegendContext}`,
            sender: 'system',
            timestamp: new Date(),
            type: 'info',
          },
        ]);

        handleRangeBasedColorChanges(rangeChanges);
        return;
      }

      // Apply color changes based on the action type
      switch (action) {
        case 'changeColorScheme':
          if (colorScheme) {
            // Force target to 'both' for color scheme changes to ensure both map and legend are updated
            const effectiveTarget = target === 'legend' ? 'legend' : 'both';
            applyColorScheme(colorScheme, effectiveTarget);
          }
          break;

        case 'applyCustomColors':
          if (colors && Array.isArray(colors) && colors.length > 0) {
            // Force target to 'both' for custom color changes to ensure both map and legend are updated
            const effectiveTarget = target === 'legend' ? 'legend' : 'both';
            applyCustomColors(colors, effectiveTarget);
          }
          break;

        case 'resetColors':
          // Reset colors to default for both map and legend
          resetColors(target || 'both');
          break;

        case 'changeLegendColors':
          // Change legend colors specifically
          changeLegendColors(colorScheme || colors, target || 'legend');
          break;

        case 'changeMapColors':
          // Change map colors specifically
          changeMapColors(colorScheme || colors, target || 'map');
          break;

        default:
          // Handle legacy actions for backward compatibility
          if (action === 'resetColors') {
            // Reset to default colors
            setLegendSettings({
              title: '',
              colors: [],
              isCustomized: false,
              isProgrammaticChange: false,
            });

            setChatMessages(prev => [
              ...prev,
              {
                text: `🎨 Colors reset to default scheme`,
                sender: 'system',
                timestamp: new Date(),
                type: 'success',
              },
            ]);
            return;
          }

          // Generate appropriate colors based on the action and colorScheme
          let newColors = [];

          if (colors && Array.isArray(colors) && colors.length > 0) {
            // Use provided custom colors
            newColors = colors.map((color, index) => ({
              label: `Class ${index + 1}`,
              color: color.startsWith('#') ? color : `#${color}`,
              range: `Custom ${index + 1}`,
              index: index,
            }));
          } else if (colorScheme) {
            // Generate colors based on color scheme
            newColors = generateColorsFromScheme(colorScheme);
          } else {
            // Default color generation
            newColors = generateDefaultColors();
          }

          if (newColors.length > 0) {
            // Update legend settings with new colors
            setLegendSettings(prev => ({
              ...prev,
              colors: newColors,
              isCustomized: true,
              isProgrammaticChange: false,
            }));

            // Apply the colors immediately
            applyCustomLegendColors();

            setChatMessages(prev => [
              ...prev,
              {
                text: `🎨 Applied ${
                  colorScheme || 'custom'
                } color scheme: ${description}`,
                sender: 'system',
                timestamp: new Date(),
                type: 'success',
              },
            ]);
          }
          break;
      }
    } catch (error) {
      console.error('Error handling color change:', error);
      setChatMessages(prev => [
        ...prev,
        {
          text: `❌ Error applying color changes: ${error.message}`,
          sender: 'system',
          timestamp: new Date(),
          type: 'error',
        },
      ]);
    }
  };

  /**
   * Get current legend context for LLM reference
   * @returns {string} Formatted string describing current legend structure
   */
  const getCurrentLegendContext = () => {
    const currentColors = legendSettings.colors;

    if (!currentColors || currentColors.length === 0) {
      return 'No legend colors currently set. Will generate defaults.';
    }

    let context = `Legend has ${currentColors.length} ranges:\n`;

    currentColors.forEach((colorInfo, index) => {
      const position =
        index === 0
          ? 'FIRST'
          : index === currentColors.length - 1
          ? 'LAST'
          : index === Math.floor(currentColors.length / 2)
          ? 'MIDDLE'
          : `POSITION ${index + 1}`;

      context += `• ${position}: ${colorInfo.range || colorInfo.label} (${
        colorInfo.color
      })\n`;
    });

    context += `\nUse "first", "last", "middle", or specific position numbers for precise changes.`;

    return context;
  };

  /**
   * Handle specific range-based color changes
   * @param {Object} rangeChanges - Object with range specifications and colors
   * @param {Object} rangeChanges.first - Color for the first range
   * @param {Object} rangeChanges.last - Color for the last range
   * @param {Object} rangeChanges.specific - Object with specific range indices and colors
   */
  const handleRangeBasedColorChanges = rangeChanges => {
    try {
      // Get current legend settings
      const currentColors = legendSettings.colors;

      if (!currentColors || currentColors.length === 0) {
        // If no current colors, generate default ones first
        const defaultColors = generateDefaultColors();

        // Set the default colors first
        setLegendSettings(prev => ({
          ...prev,
          colors: defaultColors,
          isCustomized: true,
        }));

        // Now apply the range changes to the default colors
        let updatedColors = [...defaultColors];
        let changesMade = false;

        // Handle "first" range change
        if (rangeChanges.first && rangeChanges.first.color) {
          if (updatedColors.length > 0) {
            updatedColors[0].color = normalizeColor(rangeChanges.first.color);
            // Don't modify the range label to preserve original mapping
            changesMade = true;
          }
        }

        // Handle "last" range change
        if (rangeChanges.last && rangeChanges.last.color) {
          if (updatedColors.length > 0) {
            const lastIndex = updatedColors.length - 1;
            updatedColors[lastIndex].color = normalizeColor(
              rangeChanges.last.color,
            );
            // Don't modify the range label to preserve original mapping
            changesMade = true;
          }
        }

        // Handle specific range changes by index
        if (rangeChanges.specific) {
          Object.keys(rangeChanges.specific).forEach(rangeIndex => {
            const index = parseInt(rangeIndex);
            const rangeData = rangeChanges.specific[rangeIndex];

            if (index >= 0 && index < updatedColors.length && rangeData.color) {
              updatedColors[index].color = normalizeColor(rangeData.color);
              // Don't modify the range label to preserve original mapping
              changesMade = true;
            }
          });
        }

        // Handle position-based changes
        if (rangeChanges.positions) {
          Object.keys(rangeChanges.positions).forEach(position => {
            const rangeData = rangeChanges.positions[position];
            let targetIndex = -1;

            switch (position.toLowerCase()) {
              case 'first':
                targetIndex = 0;
                break;
              case 'second':
                targetIndex = 1;
                break;
              case 'third':
                targetIndex = 2;
                break;
              case 'middle':
                targetIndex = Math.floor(updatedColors.length / 2);
                break;
              case 'last':
                targetIndex = updatedColors.length - 1;
                break;
              default:
                const parsedIndex = parseInt(position);
                if (
                  !isNaN(parsedIndex) &&
                  parsedIndex >= 0 &&
                  parsedIndex < updatedColors.length
                ) {
                  targetIndex = parsedIndex;
                }
            }

            if (targetIndex >= 0 && rangeData.color) {
              updatedColors[targetIndex].color = normalizeColor(
                rangeData.color,
              );
              // Don't modify the range label to preserve original mapping
              changesMade = true;
            }
          });
        }

        if (changesMade) {
          // Update legend settings with modified colors
          setLegendSettings(prev => ({
            ...prev,
            colors: updatedColors,
            isCustomized: true,
            isProgrammaticChange: true,
          }));

          // Apply the colors to both legend and map immediately
          applyCustomLegendColors();
          // Reset the programmatic change flag after applying colors
          setLegendSettings(prev => ({
            ...prev,
            isProgrammaticChange: false,
          }));
          // Don't call applyColorsToMap here - applyCustomLegendColors handles the map updates
        }

        return;
      }

      // Create a deep copy of current colors to avoid mutating the original
      let updatedColors = currentColors.map(color => ({ ...color }));
      let changesMade = false;

      // Handle "first" range change
      if (rangeChanges.first && rangeChanges.first.color) {
        if (updatedColors.length > 0) {
          updatedColors[0].color = normalizeColor(rangeChanges.first.color);
          // Don't modify the range label to preserve original mapping
          changesMade = true;
        }
      }

      // Handle "last" range change
      if (rangeChanges.last && rangeChanges.last.color) {
        if (updatedColors.length > 0) {
          const lastIndex = updatedColors.length - 1;
          updatedColors[lastIndex].color = normalizeColor(
            rangeChanges.last.color,
          );
          // Don't modify the range label to preserve original mapping
          changesMade = true;
        }
      }

      // Handle specific range changes by index
      if (rangeChanges.specific) {
        Object.keys(rangeChanges.specific).forEach(rangeIndex => {
          const index = parseInt(rangeIndex);
          const rangeData = rangeChanges.specific[rangeIndex];

          if (index >= 0 && index < updatedColors.length && rangeData.color) {
            updatedColors[index].color = normalizeColor(rangeData.color);
            // Don't modify the range label to preserve original mapping
            changesMade = true;
          }
        });
      }

      // Handle specific range changes by position (e.g., "middle", "second", "third")
      if (rangeChanges.positions) {
        Object.keys(rangeChanges.positions).forEach(position => {
          const rangeData = rangeChanges.positions[position];
          let targetIndex = -1;

          switch (position.toLowerCase()) {
            case 'first':
              targetIndex = 0;
              break;
            case 'second':
              targetIndex = 1;
              break;
            case 'third':
              targetIndex = 2;
              break;
            case 'middle':
              targetIndex = Math.floor(updatedColors.length / 2);
              break;
            case 'last':
              targetIndex = updatedColors.length - 1;
              break;
            default:
              // Try to parse as a number
              const parsedIndex = parseInt(position);
              if (
                !isNaN(parsedIndex) &&
                parsedIndex >= 0 &&
                parsedIndex < updatedColors.length
              ) {
                targetIndex = parsedIndex;
              }
          }

          if (
            targetIndex >= 0 &&
            targetIndex < updatedColors.length &&
            rangeData.color
          ) {
            updatedColors[targetIndex].color = normalizeColor(rangeData.color);
            // Don't modify the range label to preserve original mapping
            changesMade = true;
          }
        });
      }

      if (changesMade) {
        // Update legend settings with modified colors
        setLegendSettings(prev => ({
          ...prev,
          colors: updatedColors,
          isCustomized: true,
          isProgrammaticChange: true,
        }));

        // Apply the colors immediately to both legend and map
        applyCustomLegendColors();
        // Reset the programmatic change flag after applying colors
        setLegendSettings(prev => ({
          ...prev,
          isProgrammaticChange: false,
        }));

        // Generate a description of what was changed
        const changeDescriptions = [];
        if (rangeChanges.first) changeDescriptions.push('first range');
        if (rangeChanges.last) changeDescriptions.push('last range');
        if (rangeChanges.specific) changeDescriptions.push('specific ranges');
        if (rangeChanges.positions)
          changeDescriptions.push('position-based ranges');

        setChatMessages(prev => [
          ...prev,
          {
            text: `🎨 Updated ${changeDescriptions.join(
              ', ',
            )} colors as requested`,
            sender: 'system',
            timestamp: new Date(),
            type: 'success',
          },
        ]);
      }
    } catch (error) {
      console.error('Error handling range-based color changes:', error);
      setChatMessages(prev => [
        ...prev,
        {
          text: `❌ Error updating specific range colors: ${error.message}`,
          sender: 'system',
          timestamp: new Date(),
          type: 'error',
        },
      ]);
    }
  };

  /**
   * Normalize color input to hex format
   * @param {string} color - Color input (hex, name, or other format)
   * @returns {string} Normalized hex color
   */
  const normalizeColor = color => {
    // If it's already a hex color, return as is
    if (color.startsWith('#')) {
      return color;
    }

    // Handle common color names
    const colorMap = {
      red: '#ff0000',
      blue: '#0000ff',
      green: '#00ff00',
      yellow: '#ffff00',
      purple: '#800080',
      orange: '#ffa500',
      pink: '#ffc0cb',
      brown: '#a52a2a',
      black: '#000000',
      white: '#ffffff',
      gray: '#808080',
      grey: '#808080',
      cyan: '#00ffff',
      magenta: '#ff00ff',
      lime: '#00ff00',
      navy: '#000080',
      teal: '#008080',
      olive: '#808000',
      maroon: '#800000',
      fuchsia: '#ff00ff',
      aqua: '#00ffff',
      silver: '#c0c0c0',
      gold: '#ffd700',
      indigo: '#4b0082',
      violet: '#ee82ee',
      coral: '#ff7f50',
      salmon: '#fa8072',
      khaki: '#f0e68c',
      plum: '#dda0dd',
      azure: '#f0ffff',
      ivory: '#fffff0',
      wheat: '#f5deb3',
      snow: '#fffafa',
      mint: '#f5fffa',
      lavender: '#e6e6fa',
    };

    const lowerColor = color.toLowerCase();
    if (colorMap[lowerColor]) {
      return colorMap[lowerColor];
    }

    // If it's a 3 or 6 character hex without #, add it
    if (/^[0-9A-Fa-f]{3}$/.test(color) || /^[0-9A-Fa-f]{6}$/.test(color)) {
      return `#${color}`;
    }

    // Default fallback
    return '#808080';
  };

  /**
   * Generate colors based on a specific color scheme
   * @param {string} scheme - The color scheme name
   * @returns {Array} Array of color objects
   */
  const generateColorsFromScheme = scheme => {
    const colorSchemes = {
      blue: ['#e3f2fd', '#90caf9', '#42a5f5', '#1976d2', '#0d47a1'],
      red: ['#ffebee', '#ffcdd2', '#ef9a9a', '#e57373', '#f44336'],
      green: ['#ffebee', '#ffcdd2', '#ef9a9a', '#e57373', '#4caf50'],
      purple: ['#f3e5f5', '#e1bee7', '#ce93d8', '#ba68c8', '#9c27b0'],
      orange: ['#fff3e0', '#ffe0b2', '#ffcc80', '#ffb74d', '#ff9800'],
      grayscale: ['#f5f5f5', '#e0e0e0', '#bdbdbd', '#757575', '#424242'],
      rainbow: ['#ff0000', '#ff8000', '#ffff00', '#80ff00', '#00ff00'],
      heat: ['#0000ff', '#00ffff', '#ffff00', '#ff8000', '#ff0000'],
      cool: ['#ff0000', '#ff0080', '#8000ff', '#0080ff', '#00ffff'],
      warm: ['#ff0000', '#ff4000', '#ff8000', '#ffc000', '#ffff00'],
      earth: ['#8b4513', '#a0522d', '#cd853f', '#deb887', '#f5deb3'],
      pastels: ['#ffb3ba', '#baffc9', '#bae1ff', '#ffffba', '#ffb3f0'],
      vibrant: ['#ff1744', '#00e676', '#2196f3', '#ff9800', '#9c27b0'],
      muted: ['#795548', '#607d8b', '#9e9e9e', '#bdbdbd', '#e0e0e0'],
    };

    const schemeColors =
      colorSchemes[scheme.toLowerCase()] || colorSchemes.blue;
    const count = mapSettings.classBreaksCount;

    return schemeColors.slice(0, count).map((color, index) => ({
      label: `Class ${index + 1}`,
      title: `Class ${index + 1}`,
      color: color,
      range: `${scheme} ${index + 1}`,
      index: index,
    }));
  };

  /**
   * Generate default colors when no specific scheme is provided
   * @returns {Array} Array of default color objects
   */
  const generateDefaultColors = () => {
    const defaultColors = [
      '#313695',
      '#4575b4',
      '#74add1',
      '#abd9e9',
      '#e0f3f8',
    ];
    const count = mapSettings.classBreaksCount;

    return defaultColors.slice(0, count).map((color, index) => ({
      label: `Class ${index + 1}`,
      color: color,
      range: `Default ${index + 1}`,
      index: index,
    }));
  };

  /**
   * Apply a color scheme to the map
   * @param {string} scheme - The color scheme name
   * @param {string} target - What to change colors for (map, legend, both)
   */
  const applyColorScheme = (scheme, target = 'map') => {
    try {
      const newColors = generateColorsFromScheme(scheme);

      if (target === 'map' || target === 'both') {
        // Apply colors to the map renderer
        applyColorsToMap(newColors);
      }

      if (target === 'legend' || target === 'both') {
        // Update legend settings
        setLegendSettings(prev => ({
          ...prev,
          colors: newColors,
          isCustomized: true,
        }));

        // Apply the colors to the actual map legend
        applyCustomLegendColors();
      }
    } catch (error) {
      console.error('Error applying color scheme:', error);
    }
  };

  /**
   * Apply custom colors to the map
   * @param {string[]} colors - Array of hex color codes
   * @param {string} target - What to change colors for (map, legend, both)
   */
  const applyCustomColors = (colors, target = 'map') => {
    try {
      const newColors = colors.map((color, index) => ({
        label: `Class ${index + 1}`,
        title: `Class ${index + 1}`,
        color: color,
        range: `Custom ${index + 1}`,
        index: index,
      }));

      if (target === 'map' || target === 'both') {
        // Apply colors to the map renderer
        applyColorsToMap(newColors);
      }

      if (target === 'legend' || target === 'both') {
        // Update legend settings
        setLegendSettings(prev => ({
          ...prev,
          colors: newColors,
          isCustomized: true,
        }));

        // Apply the colors to the actual map legend
        applyCustomLegendColors();
      }
    } catch (error) {
      console.error('Error applying custom colors:', error);
    }
  };

  /**
   * Reset colors to default
   * @param {string} target - What to reset colors for (map, legend, both)
   */
  const resetColors = (target = 'map') => {
    try {
      const defaultColors = generateDefaultColors();

      if (target === 'map' || target === 'both') {
        // Apply default colors to the map renderer
        applyColorsToMap(defaultColors);
      }

      if (target === 'legend' || target === 'both') {
        // Reset legend settings
        setLegendSettings(prev => ({
          ...prev,
          colors: defaultColors,
          isCustomized: false,
        }));

        // Apply the colors to the actual map legend
        applyCustomLegendColors();
      }
    } catch (error) {
      console.error('Error resetting colors:', error);
    }
  };

  /**
   * Change legend colors specifically
   * @param {string|string[]} colors - Color scheme name or array of hex colors
   * @param {string} target - What to change colors for (legend, both)
   */
  const changeLegendColors = (colors, target = 'legend') => {
    try {
      let newColors;

      if (typeof colors === 'string') {
        // It's a color scheme name
        newColors = generateColorsFromScheme(colors);
      } else if (Array.isArray(colors)) {
        // It's an array of hex colors
        newColors = colors.map((color, index) => ({
          label: `Class ${index + 1}`,
          title: `Class ${index + 1}`,
          color: color,
          range: `Legend ${index + 1}`,
          index: index,
        }));
      } else {
        console.warn('Invalid colors parameter for legend change');
        return;
      }

      // Update legend settings
      setLegendSettings(prev => ({
        ...prev,
        colors: newColors,
        isCustomized: true,
      }));

      // Apply the colors to the actual map legend
      applyCustomLegendColors();
    } catch (error) {
      console.error('Error changing legend colors:', error);
    }
  };

  /**
   * Change map colors specifically
   * @param {string|string[]} colors - Color scheme name or array of hex colors
   * @param {string} target - What to change colors for (map, both)
   */
  const changeMapColors = (colors, target = 'map') => {
    try {
      let newColors;

      if (typeof colors === 'string') {
        // It's a color scheme name
        newColors = generateColorsFromScheme(colors);
      } else if (Array.isArray(colors)) {
        // It's an array of hex colors
        newColors = colors.map((color, index) => ({
          label: `Class ${index + 1}`,
          title: `Class ${index + 1}`,
          color: color,
          range: `Map ${index + 1}`,
          index: index,
        }));
      } else {
        console.warn('Invalid colors parameter for map change');
        return;
      }

      if (target === 'map' || target === 'both') {
        // Apply colors to the map renderer
        applyColorsToMap(newColors);
      }
    } catch (error) {
      console.error('Error changing map colors:', error);
    }
  };

  /**
   * Helper function to apply colors to the map renderer
   * @param {Array} colors - Array of color objects
   */
  const applyColorsToMap = colors => {
    try {
      const view = mapViewRef.current;
      if (!view || !view.map) return;

      const map = view.map;
      const customLayer = map.layers.find(l => l.type === 'feature');
      if (!customLayer || !customLayer.renderer) return;

      const renderer = customLayer.renderer;

      // Helper to normalize renderer infos
      const getRendererInfos = renderer => {
        if (renderer?.type === 'class-breaks' && renderer.classBreakInfos) {
          return { infos: renderer.classBreakInfos, key: 'classBreakInfos' };
        }
        if (renderer?.type === 'unique-value' && renderer.uniqueValueInfos) {
          return { infos: renderer.uniqueValueInfos, key: 'uniqueValueInfos' };
        }
        // Fallback for older references
        if (renderer?.infos) {
          return { infos: renderer.infos, key: 'infos' };
        }
        return { infos: null, key: null };
      };

      const { infos, key } = getRendererInfos(renderer);
      if (!infos || !key) return;

      // Convert hex to ESRI rgba array
      const hexToEsriRgba = (hex, alpha = 1) => {
        const h = hex.startsWith('#') ? hex.slice(1) : hex;
        const r = parseInt(h.slice(0, 2), 16) || 0;
        const g = parseInt(h.slice(2, 4), 16) || 0;
        const b = parseInt(h.slice(4, 6), 16) || 0;
        return [r, g, b, alpha];
      };

      // Apply colors to each renderer info
      infos.forEach((info, index) => {
        if (colors[index]) {
          const rgba = hexToEsriRgba(colors[index].color);
          if (info.symbol) {
            info.symbol.color = rgba;
          }
        }
      });

      // Force the layer to refresh
      customLayer.refresh();
    } catch (error) {
      console.error('Error applying colors to map:', error);
    }
  };

  useEffect(() => {
    isMounted.current = true;

    // Explicitly reconnect the socket when component mounts
    socketService.connect(() => {
      if (isMounted.current) {
        setChatMessages(prev => [
          ...prev,
          {
            text: 'Connected to the server! 🗺️',
            sender: 'system',
            timestamp: new Date(),
            type: 'success',
          },
        ]);
      }
    });

    // Setup event listeners
    socketService.onChatResponse(response => {
      setIsTyping(false); // Hide typing animation when response is received

      if (isMounted.current) {
        if (!conversationId) {
          if (response.conversationId) {
            setConversationId(response.conversationId);
          }
        }

        if (response.type === 'action') {
          // for displaying field data from LLM tool calls
          if (response.action.name === 'displayFieldsInUI') {
            const fieldData = response.action.payload;

            handleFieldDisplay(fieldData);
          }

          // for changing map colors from LLM tool calls
          if (response.action.name === 'changeMapColors') {
            const colorData = response.action.payload;
            handleColorChange(colorData);
          }

          // displayFieldChoices (new action)
          if (response.action.name === 'displayFieldChoices') {
            const payload = response.action.payload;
            const choicesMessage = {
              text: `🔎 Found ${payload?.allCount || 0} matching fields for "${
                payload?.searchTerm || ''
              }". Showing Top Picks below.`,
              sender: 'assistant',
              timestamp: new Date(),
              type: 'info',
              fieldChoices: payload,
            };
            // Add message and expand Top Picks by default for this message
            setChatMessages(prev => {
              const newIndex = prev.length;
              setExpandedSections(prevMap => {
                const next = new Map(prevMap);
                const key = `msg-${newIndex}`;
                const sectionSet = new Set(next.get(key) || []);
                sectionSet.add('topPicks');
                next.set(key, sectionSet);
                return next;
              });
              return [...prev, choicesMessage];
            });
            return;
          }

          // addFieldToTemplate (new action)
          if (response.action.name === 'addFieldToTemplate') {
            const payload = response.action.payload;
            if (onAddFieldToTemplate && payload) {
              const blockData = {
                title: payload.title || 'Field',
                outfields: Array.isArray(payload.outfields)
                  ? payload.outfields
                  : [payload.outfields],
                serviceURL: payload.serviceURL,
                group: payload.group || 'Map Layer',
                status: true,
                prefix: null,
                suffix: null,
              };

              const targetSectionKey = payload.sectionKey || sectionKey;
              onAddFieldToTemplate(blockData, targetSectionKey);

              setChatMessages(prev => [
                ...prev,
                {
                  text: `✅ Field "${blockData.title}" has been added to the template as a data block in the ${targetSectionKey} section.`,
                  sender: 'system',
                  timestamp: new Date(),
                  type: 'success',
                },
              ]);
            }
            return;
          }

          // Add the action message to chat if it has content
          if (response.content && response.content.trim() !== '') {
            setChatMessages(prev => [
              ...prev,
              {
                text: response.content,
                sender: 'assistant',
                timestamp: new Date(),
                type: 'text',
              },
            ]);
          }
          return;
        }

        // Handle error responses
        if (response.type === 'error') {
          setChatMessages(prev => [
            ...prev,
            {
              text:
                response.error?.message ||
                'An error occurred while processing your request.',
              sender: 'system',
              timestamp: new Date(),
              type: 'error',
            },
          ]);
          return;
        }

        // Only add messages that have content to avoid empty messages
        const hasContent =
          (response.content && response.content.trim() !== '') ||
          (response.text && response.text.trim() !== '') ||
          (response.message && response.message.trim() !== '');

        if (hasContent) {
          setChatMessages(prev => [
            ...prev,
            {
              ...response,
              timestamp: new Date(response.timestamp),
              sender:
                response.sender === 'ai-assistant'
                  ? 'assistant'
                  : response.sender, // Normalize sender
            },
          ]);
        } else {
          // Log when we receive a response without content for debugging
        }
      } else {
      }
    });

    // Add typing indicator listener
    socketService.onChatTyping(() => {
      setIsTyping(true);
    });

    socketService.onChatError(error => {
      console.error('Chat error:', error);
      if (isMounted.current) {
        setChatMessages(prev => [
          ...prev,
          {
            text:
              error.message || 'A connection error occurred. Please try again.',
            sender: 'system',
            timestamp: new Date(),
            type: 'error',
          },
        ]);
      }
    });

    // Cleanup function
    return () => {
      isMounted.current = false;
      socketService.disconnect();
    };
  }, []);

  // Update map configuration when settings change
  useEffect(() => {
    if (isMapLoaded) {
      updateMapConfiguration();
    }
  }, [mapSettings]);

  // Handle map settings updates without reinitializing the entire map
  useEffect(() => {
    if (isMapLoaded && !defaultSettings && selectedBlock) {
      // Update existing map with new settings without reinitializing
      // Only for custom block maps, not default maps
      updateMapWithNewSettings();
    }
  }, [mapSettings.basemap, mapSettings.layerOpacity]);

  // Auto-apply legend colors when they change
  useEffect(() => {
    if (legendSettings.isCustomized && legendSettings.colors.length > 0) {
      // Only auto-apply if this is not a programmatic change from chat
      // The chat color changes are handled explicitly in handleRangeBasedColorChanges
      if (!legendSettings.isProgrammaticChange) {
        // Add a small delay to prevent race conditions with other color updates
        const timeoutId = setTimeout(() => {
          applyCustomLegendColors();
        }, 50);

        return () => clearTimeout(timeoutId);
      } else {
      }
    }
  }, [legendSettings.colors]);

  // When map finishes loading, apply saved custom colors (e.g. after reopening template)
  // so colors show even if legendSettings were synced before the map was ready
  useEffect(() => {
    if (
      isMapLoaded &&
      !defaultSettings &&
      selectedBlock &&
      legendSettings.isCustomized &&
      legendSettings.colors?.length > 0
    ) {
      const timeoutId = setTimeout(() => {
        applyCustomLegendColors();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isMapLoaded]);

  // Auto-apply legend title when it changes
  useEffect(() => {
    if (legendSettings.isCustomized && legendSettings.title) {
      applyCustomLegendColors();
    }
  }, [legendSettings.title]);

  const updateMapConfiguration = async () => {
    try {
      setChatMessages(prev => [
        ...prev,
        {
          text: `✨ Map settings updated: ${
            mapSettings.basemap
          } basemap, ${Math.round(mapSettings.layerOpacity * 100)}% opacity`,
          sender: 'system',
          timestamp: new Date(),
          type: 'success',
        },
      ]);

      // Update map config for parent components
      setMapConfig(mapSettings);
    } catch (error) {
      console.error('Error updating map configuration:', error);
    }
  };

  const updateMapWithNewSettings = async () => {
    try {
      // Get the current map view and layer
      const view = mapViewRef.current;
      if (!view || !view.map) return;

      const map = view.map;

      // Update basemap if changed (works for both default and custom maps)
      if (map.basemap !== mapSettings.basemap) {
        map.basemap = mapSettings.basemap;
      }

      // Handle custom block layers
      const customLayer = map.layers.find(l => l.type === 'feature');
      if (customLayer) {
        // Update layer opacity
        customLayer.opacity = mapSettings.layerOpacity;
      }

      // Handle default webmap layers (when no custom block is selected)
      if (defaultSettings && map.layers.length > 0) {
        // Find operational layers in the default webmap
        const operationalLayers = map.layers.filter(
          l =>
            l.type === 'feature' ||
            l.type === 'graphics' ||
            l.type === 'imagery',
        );

        // Update opacity for all operational layers
        operationalLayers.forEach(layer => {
          if (layer.opacity !== undefined) {
            layer.opacity = mapSettings.layerOpacity;
          }
        });
      }

      setChatMessages(prev => [
        ...prev,
        {
          text: `🔄 Map updated with new settings: ${
            mapSettings.basemap
          } basemap, ${Math.round(mapSettings.layerOpacity * 100)}% opacity`,
          sender: 'system',
          timestamp: new Date(),
          type: 'success',
        },
      ]);
    } catch (error) {
      console.error('Error updating map with new settings:', error);
    }
  };

  useEffect(() => {
    let view;
    let webmap = null;
    let customLayer = null;
    let map = null;
    let legend = null;
    let graphicsToRemove = [];

    const initializeMap = async () => {
      // Add timeout to prevent infinite loading
      const mapLoadTimeout = setTimeout(() => {
        if (isMounted.current && !isMapLoaded) {
          setChatMessages(prev => [
            ...prev,
            {
              text: '⚠️ Map loading timeout.',
              sender: 'system',
              timestamp: new Date(),
              type: 'error',
            },
          ]);
          // Show error overlay instead of switching/reverting
          setMapError(true);
        }
      }, 30000); // 30 second timeout

      try {
        const [
          Map,
          MapView,
          FeatureLayer,
          Legend,
          esriConfig,
          classBreaks,
          WebMap,
          Extent,
          Graphic,
          typeRendererCreator,
          colorRendererCreator,
        ] = await loadModules([
          'esri/Map',
          'esri/views/MapView',
          'esri/layers/FeatureLayer',
          'esri/widgets/Legend',
          'esri/config',
          'esri/smartMapping/statistics/classBreaks',
          'esri/WebMap',
          'esri/geometry/Extent',
          'esri/Graphic',
          'esri/smartMapping/renderers/type',
          'esri/smartMapping/renderers/color',
        ]);

        if (!llmWebmapID || !token || !mapRef.current) return;
        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: -94.590938015952,
          ymin: 39.062328001212,
          xmax: -94.588938015952,
          ymax: 39.064328001212,
          spatialReference: {
            wkid: 4326,
          },
        });

        if (defaultSettings) {
          webmap = new WebMap({
            portalItem: {
              id: llmWebmapID,
            },
            basemap: mapSettings.basemap, // Apply current basemap setting
          });

          view = new MapView({
            container: mapRef.current,
            map: webmap,
            extent: extent,
            zoom: mapSettings.zoomLevel,
            navigation: {
              mouseWheelZoomEnabled: mapSettings.mouseWheelZoom,
            },
          });

          // Store reference to the view
          mapViewRef.current = view;

          view.when(() => {
            if (isMounted.current) {
              clearTimeout(mapLoadTimeout); // Clear timeout on successful load
              setIsMapLoaded(true);
              setMapError(false);

              // Apply current map settings to the default webmap layers
              if (webmap && webmap.layers) {
                webmap.layers.forEach(layer => {
                  if (
                    layer.type === 'feature' ||
                    layer.type === 'graphics' ||
                    layer.type === 'imagery'
                  ) {
                    if (layer.opacity !== undefined) {
                      layer.opacity = mapSettings.layerOpacity;
                    }
                  }
                });
              }
            }
          });

          if (mapSettings.showLegend) {
            legend = new Legend({
              view: view,
            });
            view.ui.add(legend, mapSettings.legendPosition);
          }

          const customSymbol = {
            type: 'picture-marker',
            url: marker,
            width: '50px',
            height: '100px',
          };
          const center = {
            longitude: (extent.xmin + extent.xmax) / 2,
            latitude: (extent.ymin + extent.ymax) / 2,
          };

          const graphic = new Graphic({
            geometry: {
              type: 'point',
              longitude: center.longitude,
              latitude: center.latitude,
            },
            symbol: customSymbol,
          });
          view.graphics.add(graphic);
          graphicsToRemove.push(graphic);
        } else {
          // Validate that selectedBlock and serviceURL exist
          if (!selectedBlock || !selectedBlock.serviceURL) {
            throw new Error(
              'Selected block or service URL is missing. Cannot create feature layer.',
            );
          }

          const defaultSym = {
            type: 'simple-fill',
            outline: {
              color: [57, 88, 92, 1], // Always show boundaries
              width: '1px',
            },
          };

          const renderer = {
            type: 'simple',
            symbol: defaultSym,
            label: `U.S. ${selectedBlock.group}`,
            visualVariables: [
              {
                type: 'color',
                field: selectedBlock.outfields[0],
                legendOptions: {},
              },
            ],
          };

          customLayer = new FeatureLayer({
            url: selectedBlock.serviceURL,
            renderer: renderer,
            title: selectedBlock.title || '',
            popupTemplate: {
              content: `{${selectedBlock.outfields[0]}}`,
              fieldInfos: [
                {
                  fieldName: selectedBlock.outfields[0],
                  format: {
                    digitSeparator: true,
                    places: 0,
                  },
                },
              ],
            },
          });

          customLayer.opacity = mapSettings.layerOpacity;
          customLayer.minScale = 0;
          customLayer.maxScale = 0;

          try {
            // Step 1: Check if the field is numeric or categorical
            await customLayer.load();
            const fieldInfo = customLayer.fieldsIndex.get(
              selectedBlock.outfields[0],
            );
            const isNumeric =
              fieldInfo &&
              [
                'integer',
                'small-integer',
                'big-integer',
                'single',
                'double',
              ].includes(fieldInfo.type);

            // Step 2: Create the appropriate renderer based on field type
            if (isNumeric) {
              const params = {
                layer: customLayer,
                field: selectedBlock.outfields[0],
                classificationMethod: mapSettings.classificationType,
                numClasses: mapSettings.classBreaksCount,
                basemap: mapSettings.basemap,

                legendOptions: {
                  title: selectedBlock.mapTitle || selectedBlock.title,
                },
              };
              const rendererResponse =
                await colorRendererCreator.createClassBreaksRenderer(params);
              customLayer.renderer = rendererResponse.renderer;
            }
            // string field types
            else {
              if (isMounted.current) {
                setIsCategorical(true);
              }

              const { renderer } = await typeRendererCreator.createRenderer({
                layer: customLayer,
                view: view,
                field: selectedBlock.outfields[0],
                legendOptions: {
                  title: selectedBlock.mapTitle || selectedBlock.outfields[0],
                },
              });
              customLayer.renderer = renderer;
            }

            // Step 3: Set the layer's renderer and add it to the map
            map = new Map({
              basemap: mapSettings.basemap,
              layers: [customLayer],
            });

            view = new MapView({
              container: mapRef.current,
              map: map,
              extent: extent,
              navigation: {
                mouseWheelZoomEnabled: mapSettings.mouseWheelZoom,
              },
              zoom: mapSettings.zoomLevel,
              scale:
                customLayer.minScale === 0
                  ? customLayer.maxScale
                  : customLayer.minScale,
            });

            // Store reference to the view
            mapViewRef.current = view;

            view.when(() => {
              if (isMounted.current) {
                clearTimeout(mapLoadTimeout); // Clear timeout on successful load
                setIsMapLoaded(true);
                // Add a message to chat when map is loaded
                setMapError(false);
                setChatMessages(prev => [
                  ...prev,
                  {
                    text: `✅ Map loaded successfully! Now displaying ${selectedBlock.title} with enhanced visualization.`,
                    sender: 'system',
                    timestamp: new Date(),
                    type: 'success',
                  },
                ]);
              }
            });

            if (mapSettings.showLegend) {
              legend = new Legend({
                view: view,
              });
              view.ui.add(legend, mapSettings.legendPosition);
            }

            const customSymbol = {
              type: 'picture-marker',
              url: marker,
              width: '50px',
              height: '100px',
            };
            const center = {
              longitude: (extent.xmin + extent.xmax) / 2,
              latitude: (extent.ymin + extent.ymax) / 2,
            };

            const graphic = new Graphic({
              geometry: {
                type: 'point',
                longitude: center.longitude,
                latitude: center.latitude,
              },
              symbol: customSymbol,
            });
            view.graphics.add(graphic);
            graphicsToRemove.push(graphic);
          } catch (error) {
            console.error('Error in renderer creation:', error);
            if (isMounted.current) {
              clearTimeout(mapLoadTimeout); // Clear timeout on error
              setChatMessages(prev => [
                ...prev,
                {
                  text: '⚠️ Error creating map renderer.',
                  sender: 'system',
                  timestamp: new Date(),
                  type: 'error',
                },
              ]);

              // Show error overlay instead of switching/reverting
              setMapError(true);
            }
          }
        }
      } catch (error) {
        console.error('Error loading modules:', error);
        if (isMounted.current) {
          clearTimeout(mapLoadTimeout); // Clear timeout on error
          setChatMessages(prev => [
            ...prev,
            {
              text: '❌ Error loading map modules.',
              sender: 'system',
              timestamp: new Date(),
              type: 'error',
            },
          ]);

          // Show error overlay instead of switching/reverting
          setMapError(true);
        }
      }
    };

    initializeMap();

    return () => {
      if (view) {
        if (graphicsToRemove.length > 0) {
          graphicsToRemove.forEach(graphic => {
            if (view.graphics) {
              view.graphics.remove(graphic);
            }
          });
        }

        if (legend && view.ui) {
          view.ui.remove(legend);
        }

        view.container = null;
        view.destroy();
      }

      if (map) {
        if (customLayer) {
          map.remove(customLayer);
        }
        map.destroy && map.destroy();
      }

      if (webmap && webmap.destroy) {
        webmap.destroy();
      }

      if (customLayer && customLayer.destroy) {
        customLayer.destroy();
      }

      view = null;
      map = null;
      webmap = null;
      customLayer = null;
      legend = null;
      graphicsToRemove = [];
      mapViewRef.current = null;
    };
  }, [
    llmWebmapID,
    token,
    selectedBlock,
    defaultSettings,
    currentPage,
    offset,
    // Remove mapSettings from dependencies to prevent map reinitialization on every setting change
    // mapSettings,
  ]);

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
    setOffset(prev => prev + 3500);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
    setOffset(prev => Math.max(0, prev - 3500));
  };

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;

    const userMessage = socketService.sendChatMessage(
      messageInput,
      conversationId,
    );
    setChatMessages(prev => [...prev, userMessage]);
    setIsTyping(true); // Show typing animation when sending message

    // Process message to detect map customization commands
    const processUserCommand = text => {
      const lowerText = text.toLowerCase();

      // Check for basemap changes
      if (
        lowerText.includes('change basemap') ||
        lowerText.includes('set basemap')
      ) {
        if (lowerText.includes('satellite')) {
          setMapSettings(prev => ({ ...prev, basemap: 'satellite' }));
          return 'Changing basemap to satellite view.';
        } else if (
          lowerText.includes('street') ||
          lowerText.includes('streets')
        ) {
          setMapSettings(prev => ({ ...prev, basemap: 'streets-vector' }));
          return 'Changing basemap to streets view.';
        } else if (lowerText.includes('dark')) {
          setMapSettings(prev => ({ ...prev, basemap: 'dark-gray-vector' }));
          return 'Changing basemap to dark gray view.';
        } else if (lowerText.includes('gray')) {
          setMapSettings(prev => ({ ...prev, basemap: 'gray-vector' }));
          return 'Changing basemap to gray view.';
        } else if (
          lowerText.includes('osm') ||
          lowerText.includes('open street map')
        ) {
          setMapSettings(prev => ({ ...prev, basemap: 'osm' }));
          return 'Changing basemap to OpenStreetMap.';
        }
      }

      // Check for opacity changes
      if (lowerText.includes('opacity') || lowerText.includes('transparent')) {
        if (lowerText.includes('increase')) {
          setMapSettings(prev => ({
            ...prev,
            layerOpacity: Math.min(1, prev.layerOpacity + 0.2),
          }));
          return 'Increasing layer opacity.';
        } else if (lowerText.includes('decrease')) {
          setMapSettings(prev => ({
            ...prev,
            layerOpacity: Math.max(0, prev.layerOpacity - 0.2),
          }));
          return 'Decreasing layer opacity.';
        } else {
          // Try to extract a percentage
          const percentMatch = lowerText.match(/(\d+)%/);
          if (percentMatch && percentMatch[1]) {
            const percent = parseInt(percentMatch[1]);
            if (percent >= 0 && percent <= 100) {
              setMapSettings(prev => ({
                ...prev,
                layerOpacity: percent / 100,
              }));
              return `Setting layer opacity to ${percent}%.`;
            }
          }
        }
      }

      // Check for zoom level changes
      if (lowerText.includes('zoom')) {
        if (lowerText.includes('in')) {
          setMapSettings(prev => ({ ...prev, zoomLevel: prev.zoomLevel + 1 }));
          return 'Zooming in.';
        } else if (lowerText.includes('out')) {
          setMapSettings(prev => ({
            ...prev,
            zoomLevel: Math.max(1, prev.zoomLevel - 1),
          }));
          return 'Zooming out.';
        }
      }

      // Check for legend customization commands
      if (lowerText.includes('legend') || lowerText.includes('color')) {
        if (lowerText.includes('generate') || lowerText.includes('create')) {
          generateLegendColors();
          return 'Generating legend colors with actual data ranges.';
        } else if (
          lowerText.includes('apply') ||
          lowerText.includes('update')
        ) {
          applyCustomLegendColors();
          return 'Applying custom legend colors to the map.';
        } else if (lowerText.includes('reset') || lowerText.includes('clear')) {
          setLegendSettings({
            title: '',
            colors: [],
            isCustomized: false,
            isProgrammaticChange: false,
          });
          return 'Legend customization reset.';
        }
      }

      // Default response if no commands matched
      return "I'll help you customize the map. You can change the basemap, opacity, or customize legend colors and titles for selected blocks. Use the settings panel for more options.";
    };

    // // Generate AI response
    processUserCommand(messageInput);

    setMessageInput('');
  };

  const formatTime = date => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageIcon = (sender, type) => {
    if (sender === 'user') return <PersonIcon />;
    if (type === 'error') return <ErrorIcon />;
    if (type === 'welcome') return <MapIcon />;
    return <SmartToyIcon />;
  };

  const getMessageColor = type => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'welcome':
        return '#2196f3';
      case 'info':
        return '#2196f3';
      default:
        return '#6c757d';
    }
  };

  function changeSelectedBlock(newBlock) {
    if (newBlock && newBlock !== selectedBlock) {
      // Reset map state and force reload
      setSelectedBlock(newBlock);
      setDefaultSettings(false);
      setIsMapLoaded(false); // Force map reload with new field data

      // Reset categorical data state
      setIsCategorical(false);
      setUniqueValueInfos([]);
      setCurrentPage(0);
      setOffset(0);

      // Reset legend customization
      setLegendSettings({
        title: '',
        colors: [],
        isCustomized: false,
        isProgrammaticChange: false,
      });

      setChatMessages(prev => [
        ...prev,
        {
          text: `🔍 Now displaying data for ${newBlock.title}`,
          sender: 'system',
          timestamp: new Date(),
          type: 'info',
        },
      ]);
    }
  }

  /**
   * Helper function to handle field display from LLM actions
   * This function processes field data received from the LLM service and updates the map
   * to display the specified fields from the given service layer.
   *
   * @param {Object} fieldData - The field data object from the LLM
   * @param {string} fieldData.serviceURL - The URL of the service layer
   * @param {string} fieldData.title - The title of the field data being displayed
   * @param {string} fieldData.group - The layer or group name these fields belong to
   * @param {string[]} fieldData.outfields - Array of field code names to display
   */
  const handleFieldDisplay = useCallback(fieldData => {
    // Validate required fields
    if (
      !fieldData.serviceURL ||
      !fieldData.title ||
      !fieldData.group ||
      !fieldData.outfields
    ) {
      // Add mapTitle if missing
      if (!fieldData.mapTitle) {
        fieldData.mapTitle = `${fieldData.title} in ${fieldData.group}`;
      }
      setChatMessages(prev => [
        ...prev,
        {
          text: '❌ Invalid field data received. Missing required fields.',
          sender: 'system',
          timestamp: new Date(),
          type: 'error',
        },
      ]);
      return;
    }

    // Validate outfields is an array
    if (
      !Array.isArray(fieldData.outfields) ||
      fieldData.outfields.length === 0
    ) {
      setChatMessages(prev => [
        ...prev,
        {
          text: '❌ Invalid field data received. Outfields must be a non-empty array.',
          sender: 'system',
          timestamp: new Date(),
          type: 'error',
        },
      ]);
      return;
    }

    const blockData = {
      group: fieldData.group,
      title: fieldData.title,
      outfields: fieldData.outfields,
      serviceURL: fieldData.serviceURL,
      mapTitle:
        fieldData.mapTitle || `${fieldData.title} in ${fieldData.group}`,
    };

    // Reset map state and force reload
    setSelectedBlock(blockData);
    setDefaultSettings(false);
    setIsMapLoaded(false); // Force map reload with new field data

    // Reset categorical data state
    setIsCategorical(false);
    setUniqueValueInfos([]);
    setCurrentPage(0);
    setOffset(0);

    setChatMessages(prev => [
      ...prev,
      {
        text: `✅ Displaying field data: ${fieldData.title} with ${fieldData.outfields.length} field(s)`,
        sender: 'system',
        timestamp: new Date(),
        type: 'success',
      },
    ]);
  }, []);

  // Handle field selection from choices
  const handleSelectFieldFromChoices = useCallback(
    async (originalIndex, messageIndex) => {
      try {
        // Get the field choices data from the message
        const message = chatMessages[messageIndex];
        if (!message?.fieldChoices?.originalResults) {
          console.error('Field choices data not found');
          return;
        }

        const fieldData = message.fieldChoices.originalResults[originalIndex];
        if (!fieldData) {
          console.error('Field data not found at index:', originalIndex);
          return;
        }

        // Check if this is a template request
        const isTemplateRequest =
          message.fieldChoices?.isTemplateRequest || false;

        // Add user message showing selection
        const fieldNumber = originalIndex + 1;
        const userMessage = {
          text: `field ${fieldNumber}`,
          sender: 'user',
          timestamp: new Date(),
          type: 'text',
        };
        setChatMessages(prev => [...prev, userMessage]);

        // If template request, add block to template instead of displaying field
        if (isTemplateRequest && onAddFieldToTemplate) {
          const blockData = {
            title:
              fieldData.displayText || fieldData.fieldName?.alias || 'Field',
            outfields: [fieldData.fieldName?.name || fieldData.fieldName],
            serviceURL: fieldData.serviceURL || fieldData.layerUrl,
            group: fieldData.layer || 'Map Layer',
            status: true,
            prefix: null,
            suffix: null,
          };

          onAddFieldToTemplate(blockData, sectionKey);

          setChatMessages(prev => [
            ...prev,
            {
              text: `✅ Field "${blockData.title}" has been added to the template as a data block.`,
              sender: 'system',
              timestamp: new Date(),
              type: 'success',
            },
          ]);
          return;
        }

        // Convert to displayFieldsInUI format (normal flow)
        const fieldDisplayData = {
          serviceURL: fieldData.serviceURL || fieldData.layerUrl,
          title: fieldData.displayText || fieldData.fieldName?.alias || 'Field',
          group: fieldData.layer || 'Map Layer',
          outfields: [fieldData.fieldName?.name || fieldData.fieldName],
          mapTitle:
            fieldData.displayText || fieldData.fieldName?.alias || 'Field',
        };

        // Display the field
        handleFieldDisplay(fieldDisplayData);
      } catch (error) {
        console.error('Error handling field selection:', error);
        setChatMessages(prev => [
          ...prev,
          {
            text: `❌ Error selecting field: ${error.message}`,
            sender: 'system',
            timestamp: new Date(),
            type: 'error',
          },
        ]);
      }
    },
    [chatMessages, handleFieldDisplay, onAddFieldToTemplate, sectionKey],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: '100vh', sm: '100vh', md: '100vh', lg: '32rem' },
        minHeight: { xs: '100vh', sm: '100vh', md: '100vh', lg: '32rem' },
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
        flexWrap: 'wrap',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <MapComponent
        key={`${llmWebmapID}-${
          selectedBlock?.title || 'default'
        }-${defaultSettings}-${selectedBlock?.serviceURL || 'default'}`}
        webmapID={llmWebmapID}
        defaultSettings={defaultSettings}
        selectedBlock={selectedBlock}
        token={token}
        mapRef={mapRef}
        isMapLoaded={isMapLoaded}
        setIsMapLoaded={setIsMapLoaded}
        isCategorical={isCategorical}
        uniqueValueInfos={uniqueValueInfos}
        currentPage={currentPage}
        offset={offset}
        setChatMessages={setChatMessages}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
        isMounted={isMounted}
        mapSettings={mapSettings}
        mapError={mapError}
        setMapError={setMapError}
      />

      <ChatComponent
        chatMessages={chatMessages}
        chatEndRef={chatEndRef}
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        formatTime={formatTime}
        getMessageIcon={getMessageIcon}
        getMessageColor={getMessageColor}
        handleSendMessage={handleSendMessage}
        mapSettings={mapSettings}
        setMapSettings={setMapSettings}
        changeSelectedBlock={changeSelectedBlock}
        handleFieldDisplay={handleFieldDisplay}
        isTyping={isTyping}
        setIsTyping={setIsTyping}
        selectedBlock={selectedBlock}
        defaultSettings={defaultSettings}
        legendSettings={legendSettings}
        setLegendSettings={setLegendSettings}
        generateLegendColors={generateLegendColors}
        applyCustomLegendColors={applyCustomLegendColors}
        applyLegendTitle={applyLegendTitle}
        handleColorChange={handleColorChange}
        onSaveCustomMap={onSaveCustomMap}
        sectionKey={sectionKey}
        webmapID={webmapID}
        setChatMessages={setChatMessages}
        toggleSectionExpanded={toggleSectionExpanded}
        isSectionExpanded={isSectionExpanded}
        handleSelectFieldFromChoices={handleSelectFieldFromChoices}
      />
    </Box>
  );
}
